import { useUpdateMenuStatusMutation } from "@me-team/host/main/menu/graphql/menu.hooks";
import { CompanyAccess, MenuStatusType } from "@me-team/host/main/graphql/types";
import { MenuActionState, PartialMenu } from "@me-team/common/src/interfaces/meMenuInterfaces/interfaces";
import { makeMenuInput } from "../helpers";
import { menuTypeMap } from "../constants";
import { useState } from "react";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

interface UseMenuStatusActionsProps {
    branchId: number;
    onSuccess?: () => Promise<void> | void;
    onError?: (error: any) => void;
    onActiveMenuError?: (menu: PartialMenu) => void;
    showToast?: (text: string) => void;
    t: (key: string) => string;
}

export const useMenuStatusActions = ({
    branchId,
    onSuccess,
    onError,
    onActiveMenuError,
    showToast,
    t,
}: UseMenuStatusActionsProps) => {
    const [menuAction, setMenuAction] = useState<MenuActionState>(null);
    const [updateMenuStatus, { loading }] = useUpdateMenuStatusMutation();
    const canCloneEditMenu = useRoleAccess(CompanyAccess.MenuCloneEdit);

    const makeStatusInput = (menu: PartialMenu, status: MenuStatusType) => {
        return makeMenuInput({
            name: menu.name,
            type: menuTypeMap[menu.type?.slug as keyof typeof menuTypeMap],
            languages: (menu.languages ?? []).map((language) => language.id),
            status,
        });
    };

    const isActiveMenuError = (error: any) =>
        error?.graphQLErrors?.some((e: any) => e?.extensions?.category === "menu.active_of_this_type_already_exists");

    const updateStatus = async (
        menu: PartialMenu,
        status: MenuStatusType,
        options?: { ignoreValidation?: boolean; successMessage?: string }
    ) => {
        if (!canCloneEditMenu) return;

        try {
            const input = makeStatusInput(menu, status);
            await updateMenuStatus({
                variables: {
                    menuId: Number(menu.id),
                    branchId,
                    input,
                    ignoreValidation: options?.ignoreValidation ?? false,
                },
            });

            await onSuccess?.();
            if (options?.successMessage) {
                showToast?.(options.successMessage);
            }
        } catch (error) {
            if (isActiveMenuError(error)) {
                onActiveMenuError?.(menu);
                return;
            }
            onError?.(error);
            console.error(error);
        }
    };

    const publishMenu = (menu: PartialMenu) =>
        updateStatus(menu, MenuStatusType.Activated, {
            successMessage: t("Published"),
        });

    const replaceMenu = (menu: PartialMenu) =>
        updateStatus(menu, MenuStatusType.Activated, {
            ignoreValidation: true,
            successMessage: t("Replaced"),
        });

    const unpublishMenu = (menu: PartialMenu) =>
        updateStatus(menu, MenuStatusType.Deactivated, {
            ignoreValidation: true,
            successMessage: t("Unpublished"),
        });

    return {
        publishMenu,
        replaceMenu,
        unpublishMenu,
        menuAction,
        setMenuAction,
        loading,
    };
};
